import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client'
import linkedProductsQuery from './query'
import { ILinkedProductsVariables, ILinkedProductsResponse } from './types'

/**
 * Hook declaration to obtain a lazy query of linked products related
 *
 * **`no-cache` is a pin, not a default, and the key order below is
 * load-bearing.** Apollo keys the cache on document plus variables, and this
 * query takes `storefrontName` and `sessionUid` only — both constant for a
 * session — so every fetch in a session is cache-identical. A caching policy
 * would serve the first answer for the rest of it, however far the cart had
 * moved: total staleness rather than occasional. The hook refetches whenever a
 * line carrying linked products is removed or reduced, as well as during the
 * place-order flow, so that divergence is live rather than a corner case.
 * Reordering the two keys is a behaviour change, not a formatting one.
 *
 * The policy options are omitted from the parameter *and* neutralised beside
 * the pin, because the type alone stops them compiling but not a caller who
 * casts, and `nextFetchPolicy` would otherwise win from the second fetch
 * onward. Apollo still lets the *execute* call override the policy, which a
 * wrapper cannot prevent without changing what it returns — nothing does that
 * here, and no caller passes one at either door.
 *
 * @param options: LazyQueryHookOptions without the fetch-policy keys
 * @internal
 */
const useLazyLinkedProducts = (
  options?: Omit<
    LazyQueryHookOptions<ILinkedProductsResponse, ILinkedProductsVariables>,
    'fetchPolicy' | 'nextFetchPolicy' | 'initialFetchPolicy'
  >,
) => {
  return useLazyQuery<ILinkedProductsResponse, ILinkedProductsVariables>(
    linkedProductsQuery,
    {
      ...options,
      fetchPolicy: 'no-cache',
      // Neutralised beside the pin, not merely omitted from the type: both
      // survive the spread otherwise, and `nextFetchPolicy` would take over
      // from the second fetch onward.
      nextFetchPolicy: undefined,
      initialFetchPolicy: undefined,
    },
  )
}

export { useLazyLinkedProducts }
